import { useApi } from '@classgo/app-kit';
import type { StudentSensitiveData } from '@classgo/shared';
import { studentSensitiveDataInputSchema } from '@classgo/shared';
import { Input } from '@classgo/ui';

import { useT } from '../i18n';
import { useForm } from '../lib/form';
import { peopleApi } from '../people/api';
import { FormShell } from './form-shell';

const nullable = (value: string) => (value.trim() === '' ? null : value.trim());

/**
 * The sensitive fields (scoping 3.14): shown only behind the permission and an active consent.
 */
export const SensitiveForm = ({
  studentId,
  current,
  onDone,
}: {
  studentId: string;
  current: StudentSensitiveData;
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const form = useForm({
    schema: studentSensitiveDataInputSchema,
    initial: {
      eps: current.eps ?? '',
      allergies: current.allergies ?? '',
      disabilities: current.disabilities ?? '',
      religion: current.religion ?? '',
      ethnicity: current.ethnicity ?? '',
    },
    toInput: (values) => ({
      eps: nullable(values.eps),
      allergies: nullable(values.allergies),
      disabilities: nullable(values.disabilities),
      religion: nullable(values.religion),
      ethnicity: nullable(values.ethnicity),
    }),
  });
  return (
    <FormShell
      error={form.errors.form}
      busy={form.busy}
      onSubmit={(event) => {
        event.preventDefault();
        void form
          .submit(async (input) => {
            await peopleApi(api).putSensitive(studentId, input);
          })
          .then((ok) => ok && onDone());
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {(['eps', 'allergies', 'disabilities', 'religion', 'ethnicity'] as const).map((key) => (
          <Input
            key={key}
            label={t(`sensitive.field.${key}`)}
            value={form.values[key]}
            onChange={(event) => form.set(key, event.target.value)}
            {...(form.fieldError(key) ? { error: form.fieldError(key) } : {})}
          />
        ))}
      </div>
    </FormShell>
  );
};
